import { forwardRef, useImperativeHandle, useRef } from "react";
import { Textarea } from "./ui/textarea";
import { Bold } from "lucide-react";
import { toggleBold } from "../lib/richText";

// Textarea with a Bold button + Ctrl/⌘+B shortcut. Bold text is stored as **text**.
export const RichTextarea = forwardRef(({ value, onChange, onKeyDown, wrapperClassName = "", boldTestId, ...props }, ref) => {
  const inner = useRef(null);
  useImperativeHandle(ref, () => inner.current);

  const applyBold = () => {
    const el = inner.current;
    const { next, selStart, selEnd } = toggleBold(el, value || "");
    onChange({ target: { value: next, selectionStart: selEnd } });
    requestAnimationFrame(() => { el?.focus(); el?.setSelectionRange(selStart, selEnd); });
  };

  const handleKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "b") {
      e.preventDefault();
      applyBold();
      return;
    }
    onKeyDown?.(e);
  };

  return (
    <div className={`relative ${wrapperClassName}`}>
      <Textarea ref={inner} value={value} onChange={onChange} onKeyDown={handleKeyDown} {...props}
        className={`pr-10 ${props.className || ""}`} />
      <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={applyBold}
        title="Bold (Ctrl/⌘+B)" aria-label="Bold"
        className="absolute top-1.5 right-1.5 h-7 w-7 inline-flex items-center justify-center rounded-md border bg-white text-zinc-700 hover:bg-zinc-100 transition-colors"
        data-testid={boldTestId || `${props["data-testid"] || "textarea"}-bold-btn`}>
        <Bold className="h-3.5 w-3.5" />
      </button>
    </div>
  );
});
RichTextarea.displayName = "RichTextarea";
